import { useMemo } from 'react';
import { keepPreviousData, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  EventSpeaker,
  EventSponsor,
  JoomlaCategories,
  JoomlaContent,
  JoomlaEventBookingCategory,
  ListResponse,
  MoodleEvent,
  MoodleEventOwner,
  MoodleGalleryImage,
  MoodleLocation,
  MoodleSpeaker,
  MoodleSponsor,
} from '../types/moodleEventBooking';
import { moodleFetch, MoodleInstance } from './useMoodleHooks';
import { CacheKey } from '../types/cache-keys';

export const JOOMLA_BASE_URL = process.env.REACT_APP_JOOMLA_BASE_URL ?? '';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnWindowFocus: true,
  refetchOnMount: 'always' as const,
};

function useFetchMoodleItems<T>(
  endpoint: string,
  key: any[],
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<T, Error> {
  return useQuery<T, Error>({
    queryKey: [...key, instance],
    queryFn: async () => {
      return await moodleFetch<T>(endpoint, params, {}, instance);
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

function buildEventsKey(
  courseId: number | string | undefined,
  page?: number,
  size?: number,
  minEventEndDate?: string,
  maxEventEndDate?: string
) {
  return [
    CacheKey.EventsByCourse,
    String(courseId ?? ''),
    [String(page ?? ''), String(size ?? ''), String(minEventEndDate ?? ''), String(maxEventEndDate ?? '')].join(':'),
  ];
}

export function useMoodleEventsByCourse(
  courseId: number | string | undefined,
  page?: number,
  size?: number,
  minEventEndDate?: string,
  maxEventEndDate?: string,
  options?: Omit<UseQueryOptions<ListResponse<MoodleEvent>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleEvent>, Error> {
  const params = useMemo(() => {
    const out: Record<string, any> = {};
    if (courseId) out['course-id'] = courseId;
    if (page !== undefined) out.page = page;
    if (size !== undefined) out.size = size;
    if (minEventEndDate) out.min_event_end_date = minEventEndDate;
    if (maxEventEndDate) out.max_event_end_date = maxEventEndDate;
    return out;
  }, [courseId, page, size, minEventEndDate, maxEventEndDate]);

  const queryKey = useMemo(
    () => buildEventsKey(courseId, page, size, minEventEndDate, maxEventEndDate),
    [courseId, page, size, minEventEndDate, maxEventEndDate]
  );

  return useFetchMoodleItems<ListResponse<MoodleEvent>>(
    'moodle/joomla/event/',
    queryKey,
    params,
    options,
    instance
  );
}

export function useMoodleLocations(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<MoodleLocation>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleLocation>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<MoodleLocation>>(
    'moodle/joomla/location/',
    ['moodleLocations', page, size],
    params,
    options,
    instance
  );
}

export function useMoodleEventOwners(
  eventId: number,
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<MoodleEventOwner>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleEventOwner>, Error> {
  const params: Record<string, any> = { event: eventId };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<MoodleEventOwner>>(
    'moodle/joomla/event-owner/',
    ['moodleEventOwners', eventId, page, size],
    params,
    options,
    instance
  );
}

export function useMoodleEventSpeakers(
  eventId: number,
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<EventSpeaker>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<EventSpeaker>, Error> {
  const params: Record<string, any> = { event: eventId };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<EventSpeaker>>(
    'moodle/joomla/event-speaker/',
    ['moodleEventSpeakers', eventId, page, size],
    params,
    options,
    instance
  );
}

export function useMoodleEventSponsors(
  eventId: number,
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<EventSponsor>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<EventSponsor>, Error> {
  const params: Record<string, any> = { event: eventId };
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<EventSponsor>>(
    'moodle/joomla/event-sponsor/',
    ['moodleEventSponsors', eventId, page, size],
    params,
    options,
    instance
  );
}

export function useMoodleSpeakers(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<MoodleSpeaker>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleSpeaker>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<MoodleSpeaker>>(
    'moodle/joomla/speaker/',
    ['moodleSpeakers', page, size],
    params,
    options,
    instance
  );
}

export function useMoodleSponsors(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<MoodleSponsor>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleSponsor>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<MoodleSponsor>>(
    'moodle/joomla/sponsor/',
    ['moodleSponsors', page, size],
    params,
    options,
    instance
  );
}

export function useMoodleGallery(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<MoodleGalleryImage>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<MoodleGalleryImage>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<MoodleGalleryImage>>(
    'moodle/joomla/gallery/',
    ['moodleGallery', page, size],
    params,
    options,
    instance
  );
}

export function useJoomlaContent(
  categoryId?: number | string,
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<JoomlaContent>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<JoomlaContent>, Error> {
  const params: Record<string, any> = {};
  if (categoryId) params.catid = categoryId;
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<JoomlaContent>>(
    'moodle/joomla/joomla-content/',
    ['joomlaContent', categoryId, page, size],
    params,
    options,
    instance
  );
}

export function useJoomlaCategories(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<JoomlaCategories>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<JoomlaCategories>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<JoomlaCategories>>(
    'moodle/joomla/joomla-categories/',
    ['joomlaCategories', page, size],
    params,
    options,
    instance
  );
}

export function useJoomlaEventBookingCategory(
  page?: number,
  size?: number,
  options?: Omit<UseQueryOptions<ListResponse<JoomlaEventBookingCategory>, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<ListResponse<JoomlaEventBookingCategory>, Error> {
  const params: Record<string, any> = {};
  if (page !== undefined) params.page = page;
  if (size !== undefined) params.size = size;
  return useFetchMoodleItems<ListResponse<JoomlaEventBookingCategory>>(
    'moodle/joomla/category/',
    ['eventBookingCategory', page, size],
    params,
    options,
    instance
  );
}

export function useMoodleEventDetails(
  eventId: number | string | undefined,
  options?: Omit<UseQueryOptions<MoodleEvent | null, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleEvent | null, Error> {
  return useFetchMoodleItems<MoodleEvent>(
    `moodle/joomla/event/${eventId}/`,
    ['moodleEventDetails', eventId],
    {},
    { ...options, enabled: !!eventId },
    instance
  );
}

export interface UserLike {
  id: number;
  name: string;
  username?: string;
  email?: string;
  [key: string]: any;
}

type Params = {
  q?: string;
  user_ids?: (number | string)[];
  usernames?: string[];
  emails?: string[];
  limit?: number;
};

function normalizeJoomlaUser(u: any): UserLike {
  const name = u?.name || u?.username || u?.email || String(u?.id);
  return {
    id: Number(u?.id),
    name,
    username: u?.username,
    email: u?.email,
    ...u,
  };
}

function dedupeById(arr: any[]): any[] {
  const seen = new Set<number>();
  const out: any[] = [];
  for (const item of arr) {
    const id = Number(item?.id);
    if (!Number.isFinite(id)) continue;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(item);
    }
  }
  return out;
}

export function useJoomlaUserSearch(
  { q = '', user_ids = [], usernames = [], emails = [], limit = 25 }: Params,
  options?: Omit<UseQueryOptions<UserLike[], Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<UserLike[], Error> {
  const instance: MoodleInstance = 'external';

  const key = [
    'JoomlaUsersSearch',
    'external',
    {
      q,
      user_ids: [...user_ids].map(String).sort(),
      usernames: [...usernames].sort(),
      emails: [...emails].sort(),
      limit,
    },
  ];

  return useQuery<UserLike[], Error>({
    queryKey: key,
    queryFn: async () => {
      const params: Record<string, any> = {
        q: q?.trim() ? q.trim() : undefined,
        user_ids: user_ids?.length ? user_ids.join(',') : undefined,
        usernames: usernames?.length ? usernames.join(',') : undefined,
        emails: emails?.length ? emails.join(',') : undefined,
        size: limit,
      };

      const data = await moodleFetch<any[]>(
        'moodle/joomla/users/search/',
        params,
        {},
        'external'
      );

      const arr = Array.isArray(data) ? data : [];
      return dedupeById(arr).map(normalizeJoomlaUser);
    },
    ...{
        placeholderData: keepPreviousData,
        staleTime: 60000,
        refetchOnWindowFocus: false,
        enabled:
          (options?.enabled ?? true) &&
          !!(
            q?.trim() ||
            (user_ids && user_ids.length) ||
            (usernames && usernames.length) ||
            (emails && emails.length)
          ),
        ...(options || {}),
      },
  });
}

export { moodleFetch } from './useMoodleHooks';


export function useMoodleTeamEventsByWindow(
  teamEmails: string[] | undefined,
  page?: number,
  size?: number,
  minEventEndDate?: string,
  maxEventEndDate?: string,
  courseId?: number | string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<any, Error> {
  const payload = useMemo(() => {
    const emails = Array.from(
      new Set((teamEmails || []).map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))
    ).sort();

    if (!emails.length) return null;

    const out: Record<string, any> = { emails };
    if (page != null) out.page = page;
    if (size != null) out.size = size;
    if (minEventEndDate) out.min_event_end_date = minEventEndDate;
    if (maxEventEndDate) out.max_event_end_date = maxEventEndDate;
    if (courseId != null && String(courseId).trim() !== '') out.course_id = courseId;

    return out;
  }, [teamEmails, page, size, minEventEndDate, maxEventEndDate, courseId]);

  return useQuery<any, Error>({
    queryKey: [
      'moodleTeamEventsByWindow',
      payload?.emails?.join(',') ?? '',
      payload?.page ?? null,
      payload?.size ?? null,
      payload?.min_event_end_date ?? null,
      payload?.max_event_end_date ?? null,
      payload?.course_id ?? null,
    ],
    queryFn: async () => {
      if (!payload) return null;
      return await moodleFetch<any>(
        'moodle/joomla/events/window/raw/team/',
        {},
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
    },
    ...{
        enabled: !!payload,
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export type CorrelatedEventPerson = {
  id?: number;
  name?: string;
  username?: string;
  email?: string;
};

export type CorrelatedEventRow = {
  id: number;
  title: string;
  event_date?: string | null;
  event_end_date?: string | null;
  location_name?: string | null;
  location_city?: string | null;
  location_state?: string | null;
  location_country?: string | null;
  registrant_count?: number;
  feedback_count?: number;
  owners?: CorrelatedEventPerson[];
  proctors?: CorrelatedEventPerson[];
  course_ids?: number[];
  moodle_course_ids?: number[];
  mappings?: {
    joomdle_courses?: number[] | number | string | null;
    [key: string]: any;
  };
  custom_fields?: Record<string, any>;
  params?: Record<string, any>;
  [key: string]: any;
};

export type CorrelatedEventSearchResponse = {
  results: CorrelatedEventRow[];
  count?: number;
  total?: number;
  page?: number;
  size?: number;
};

export type CorrelatedEventSearchParams = {
  email?: string;
  geo?: string | string[];
  country?: string | string[];
  state?: string | string[];
  category?: string | string[];
  course_id?: number | string;
  course_ids?: Array<number | string>;
  min_event_end_date?: string;
  max_event_end_date?: string;
  days_past?: number;
  days_future?: number;
  page?: number;
  size?: number;
  restrict_to_user_association?: boolean;
  user_geo?: string;
};

function mapSalesLevel1ToGeo(value?: string | null): 'amer' | 'emea' | 'apjc' | '' {
  const v = String(value ?? '').trim().toLowerCase();

  if (!v) return '';

  if (
    v === 'americas' ||
    v === 'america' ||
    v === 'amer' ||
    v.includes('americas')
  ) {
    return 'amer';
  }

  if (
    v === 'emea' ||
    v.includes('emea') ||
    v.includes('europe') ||
    v.includes('middle east') ||
    v.includes('africa')
  ) {
    return 'emea';
  }

  if (
    v === 'apjc' ||
    v.includes('apjc') ||
    v.includes('asia pacific') ||
    v.includes('japan') ||
    v.includes('greater china')
  ) {
    return 'apjc';
  }

  return '';
}

function splitMaybeArray(value?: string | string[]) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeCorrelatedEventResponse(data: any): CorrelatedEventSearchResponse {
  if (Array.isArray(data)) {
    return {
      results: data,
      count: data.length,
      total: data.length,
      page: 1,
      size: data.length,
    };
  }

  if (Array.isArray(data?.items)) {
    return {
      results: data.items,
      count: data.returned ?? data.count ?? data.items.length,
      total: data.total_count ?? data.total ?? data.count ?? data.items.length,
      page: data.page ?? 1,
      size: data.size ?? data.items.length,
    };
  }

  if (Array.isArray(data?.results)) {
    return {
      results: data.results,
      count: data.count ?? data.total ?? data.results.length,
      total: data.total ?? data.count ?? data.results.length,
      page: data.page ?? 1,
      size: data.size ?? data.results.length,
    };
  }

  if (Array.isArray(data?.events)) {
    return {
      results: data.events,
      count: data.count ?? data.total ?? data.events.length,
      total: data.total ?? data.count ?? data.events.length,
      page: data.page ?? 1,
      size: data.size ?? data.events.length,
    };
  }

  return {
    results: [],
    count: 0,
    total: 0,
    page: 1,
    size: 0,
  };
}

export function useCorrelatedEventSearch(
  params?: CorrelatedEventSearchParams,
  options?: Omit<UseQueryOptions<CorrelatedEventSearchResponse, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<CorrelatedEventSearchResponse, Error> {
  const normalizedParams = useMemo(() => {
    const out: Record<string, any> = {};

    if (params?.email) out.email = params.email;
    if (params?.restrict_to_user_association != null) {
      out.restrict_to_user_association = params.restrict_to_user_association ? 'true' : 'false';
    }
    if (params?.user_geo) {
      out.user_geo = params.user_geo;
    }

    const geos = splitMaybeArray(params?.geo);
    const countries = splitMaybeArray(params?.country);
    const states = splitMaybeArray(params?.state);
    const categories = splitMaybeArray(params?.category);

    if (geos.length) out.geo = geos.join(',');
    if (countries.length) out.country = countries.join(',');
    if (states.length) out.state = states.join(',');
    if (categories.length) out.category = categories.join(',');

    if (params?.course_id != null && String(params.course_id).trim() !== '') {
      out.course_id = params.course_id;
    }

    if (params?.course_ids?.length) {
      out.course_ids = params.course_ids.map(String).join(',');
    }

    if (params?.min_event_end_date) out.min_event_end_date = params.min_event_end_date;
    if (params?.max_event_end_date) out.max_event_end_date = params.max_event_end_date;
    if (params?.days_past != null) out.days_past = params.days_past;
    if (params?.days_future != null) out.days_future = params.days_future;
    if (params?.page != null) out.page = params.page;
    if (params?.size != null) out.size = params.size;

    return out;
  }, [params]);

  return useQuery<CorrelatedEventSearchResponse, Error>({
    queryKey: ['correlatedEventSearch', normalizedParams, instance],
    queryFn: async () => {
      const data = await moodleFetch<any>(
        'moodle/joomla/events/correlated-search/',
        normalizedParams,
        {},
        instance
      );
      return normalizeCorrelatedEventResponse(data);
    },
    ...{
        placeholderData: keepPreviousData,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        ...(options || {}),
      },
  });
}

export function useCorrelatedUserEvents(
  email: string | undefined,
  params?: Omit<CorrelatedEventSearchParams, 'email'>,
  options?: Omit<UseQueryOptions<CorrelatedEventSearchResponse, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<CorrelatedEventSearchResponse, Error> {
  return useCorrelatedEventSearch(
    {
      ...params,
      email,
    },
    {
      enabled: !!email && (options?.enabled ?? true),
      ...(options || {}),
    },
    instance
  );
}